export type ParentGuardianFormKey = 'father' | 'mother';

export type ParentGuardianHouseholdMode = 'two_parent' | 'single_parent_guardian';

export type ParentGuardianDisplayRole =
    | 'father'
    | 'mother'
    | 'grandfather'
    | 'grandmother'
    | 'legal_guardian'
    | 'other';

export type ParentGuardianPersistenceRelationship =
    | 'father'
    | 'mother'
    | 'stepfather'
    | 'stepmother'
    | 'guardian1'
    | 'guardian2';

export type ParentGuardianHouseholdComposition = {
    mode: ParentGuardianHouseholdMode;
    primaryKey: ParentGuardianFormKey;
    primaryRole: ParentGuardianDisplayRole;
    otherRoleLabel?: string;
};

export const PARENT_GUARDIAN_DISPLAY_ROLE_OPTIONS: Array<{ label: string; value: ParentGuardianDisplayRole }> = [
    { label: 'Father', value: 'father' },
    { label: 'Mother', value: 'mother' },
    { label: 'Grandfather', value: 'grandfather' },
    { label: 'Grandmother', value: 'grandmother' },
    { label: 'Legal guardian', value: 'legal_guardian' },
    { label: 'Other', value: 'other' },
];

export const DEFAULT_PARENT_GUARDIAN_HOUSEHOLD_COMPOSITION: ParentGuardianHouseholdComposition = {
    mode: 'two_parent',
    primaryKey: 'father',
    primaryRole: 'father',
};

const FORM_KEYS = new Set<ParentGuardianFormKey>(['father', 'mother']);
const MODES = new Set<ParentGuardianHouseholdMode>(['two_parent', 'single_parent_guardian']);
const DISPLAY_ROLES = new Set<ParentGuardianDisplayRole>(
    PARENT_GUARDIAN_DISPLAY_ROLE_OPTIONS.map((option) => option.value),
);

const ROLE_LABELS: Record<ParentGuardianDisplayRole, string> = {
    father: 'Father',
    mother: 'Mother',
    grandfather: 'Grandfather',
    grandmother: 'Grandmother',
    legal_guardian: 'Legal guardian',
    other: 'Parent/guardian',
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {};

const readText = (value: unknown): string => String(value ?? '').trim();

const normalizeFormKey = (value: unknown, fallback: ParentGuardianFormKey): ParentGuardianFormKey =>
    FORM_KEYS.has(value as ParentGuardianFormKey) ? value as ParentGuardianFormKey : fallback;

const normalizeMode = (value: unknown): ParentGuardianHouseholdMode =>
    MODES.has(value as ParentGuardianHouseholdMode) ? value as ParentGuardianHouseholdMode : 'two_parent';

const normalizeDisplayRole = (
    value: unknown,
    fallback: ParentGuardianDisplayRole,
): ParentGuardianDisplayRole =>
    DISPLAY_ROLES.has(value as ParentGuardianDisplayRole) ? value as ParentGuardianDisplayRole : fallback;

export const normalizeParentGuardianHouseholdComposition = (
    value: unknown,
): ParentGuardianHouseholdComposition => {
    const record = asRecord(value);
    const mode = normalizeMode(record.mode);
    const primaryKey = normalizeFormKey(record.primaryKey, 'father');
    const fallbackRole: ParentGuardianDisplayRole = primaryKey === 'mother' ? 'mother' : 'father';
    const primaryRole = mode === 'two_parent'
        ? fallbackRole
        : normalizeDisplayRole(record.primaryRole, fallbackRole);
    const otherRoleLabel = readText(record.otherRoleLabel);
    return {
        mode,
        primaryKey,
        primaryRole,
        ...(otherRoleLabel ? { otherRoleLabel } : {}),
    };
};

export const isDefaultParentGuardianHouseholdComposition = (
    value: unknown,
): boolean => {
    const normalized = normalizeParentGuardianHouseholdComposition(value);
    return normalized.mode === DEFAULT_PARENT_GUARDIAN_HOUSEHOLD_COMPOSITION.mode
        && normalized.primaryKey === DEFAULT_PARENT_GUARDIAN_HOUSEHOLD_COMPOSITION.primaryKey
        && normalized.primaryRole === DEFAULT_PARENT_GUARDIAN_HOUSEHOLD_COMPOSITION.primaryRole
        && !normalized.otherRoleLabel;
};

export const resolveParentGuardianHouseholdComposition = (
    data: unknown,
): ParentGuardianHouseholdComposition =>
    normalizeParentGuardianHouseholdComposition(asRecord(data).householdComposition);

export const resolveParentGuardianActiveKeys = (
    composition: ParentGuardianHouseholdComposition,
): ParentGuardianFormKey[] =>
    composition.mode === 'single_parent_guardian'
        ? [composition.primaryKey]
        : ['father', 'mother'];

export const isParentGuardianFormKeyActive = (
    composition: ParentGuardianHouseholdComposition,
    parentKey: ParentGuardianFormKey,
): boolean => resolveParentGuardianActiveKeys(composition).includes(parentKey);

export const resolveParentGuardianDisplayLabel = (
    composition: ParentGuardianHouseholdComposition,
    parentKey: ParentGuardianFormKey,
): string => {
    if (composition.mode === 'single_parent_guardian' && composition.primaryKey === parentKey) {
        if (composition.primaryRole === 'other' && composition.otherRoleLabel) {
            return composition.otherRoleLabel;
        }
        return ROLE_LABELS[composition.primaryRole];
    }
    return parentKey === 'father' ? 'Father' : 'Mother';
};

export const resolveParentGuardianPersistenceRelationship = (
    composition: ParentGuardianHouseholdComposition,
    parentKey: ParentGuardianFormKey,
): ParentGuardianPersistenceRelationship => {
    if (composition.mode !== 'single_parent_guardian' || composition.primaryKey !== parentKey) {
        return parentKey;
    }
    if (composition.primaryRole === 'father' || composition.primaryRole === 'mother') {
        return composition.primaryRole;
    }
    return parentKey === 'father' ? 'guardian1' : 'guardian2';
};
